import { apiRequest } from "./client";

export interface OnboardingState {
  examType: string | null;
  examYear: number | null;
  onboardingCompleted: boolean;
  parentConsentRequired: boolean;
  parentConsentApproved: boolean;
}

/**
 * Save one onboarding answer.
 *
 * Every field is optional because each screen sends only its own: the exam
 * picker sends an exam, the subject picker sends subjects. Saving per screen
 * rather than in one submit at the end means a student who closes the app
 * halfway keeps what they already answered.
 */
export function saveOnboarding(input: {
  /** BOTH creates a profile for each exam, JAMB as the primary. */
  examType?: "JAMB" | "WAEC" | "BOTH";
  /** Which exam profile the subjects, year and completion are for. */
  profile?: "JAMB" | "WAEC";
  examYear?: number;
  subjectIds?: string[];
  isUnderAge?: boolean;
  completed?: boolean;
}): Promise<OnboardingState> {
  return apiRequest<OnboardingState>("/api/me/onboarding", {
    method: "PATCH",
    body: input,
    authenticated: true,
  });
}

/** How the parent is reached. SMS and WhatsApp open the device's native app. */
export type ConsentChannel = "sms" | "whatsapp" | "email";

export interface ParentConsentRequested {
  status: string;
  channel: ConsentChannel;
  /** Normalised, so a student who mistyped can see it before they wait. */
  sentTo: string;
  /**
   * When the server will accept another send.
   */
  canResendAt: string;
  expiresAt: string;
}

/**
 * Send a parent the consent link, by whichever channel the student picked.
 *
 * The contact that goes with the channel is the caller's to get right; the
 * server checks the pairing and refuses a mismatch rather than sending into
 * nowhere.
 */
export function requestParentConsent(input: {
  channel: ConsentChannel;
  parentPhone?: string;
  parentEmail?: string;
  parentFirstName?: string;
}): Promise<ParentConsentRequested> {
  return apiRequest<ParentConsentRequested>("/api/me/parent-consent", {
    method: "POST",
    body: input,
    authenticated: true,
  });
}

export interface CatalogSubject {
  id: string;
  name: string;
  /** English is required for both exams and cannot be unpicked. */
  required: boolean;
}

/**
 * The subjects a student can actually pick.
 *
 * The picker used to choose from twelve hardcoded names whose ids matched
 * nothing in the question bank, so a pick could not have been saved even if
 * anything had tried to save it.
 */
export function fetchSubjects(examType?: "JAMB" | "WAEC"): Promise<{ subjects: CatalogSubject[] }> {
  const query = examType ? `?examType=${examType}` : "";
  return apiRequest<{ subjects: CatalogSubject[] }>(`/api/subjects${query}`);
}

/** Ask again. The server owns the cooldown and the send cap. */
export function resendParentConsent(): Promise<ParentConsentRequested> {
  return apiRequest<ParentConsentRequested>("/api/me/parent-consent/resend", {
    method: "POST",
    body: {},
    authenticated: true,
  });
}

export interface SampleQuestion {
  id: string;
  question_text: string;
  options: Record<string, string> | { id: string; text: string }[] | null;
  correct_answer: string | null;
  explanation: string | null;
  subject?: { name?: string } | null;
}

/**
 * One real question, for the waiting room.
 *
 * Public, and deliberately the same endpoint practice uses — the alternative
 * was the hardcoded algebra question that used to sit there, which would be
 * the first thing a student ever sees from us and would be invented.
 */
export function fetchSampleQuestion(
  subjectId: string,
): Promise<{ count: number; questions: SampleQuestion[] }> {
  return apiRequest<{ count: number; questions: SampleQuestion[] }>(
    `/api/questions/practice-set?subjectId=${encodeURIComponent(subjectId)}&count=1`,
  );
}

export interface ParentConsentState {
  required: boolean;
  approved: boolean;
  status: "pending" | "approved" | "rejected" | "expired" | null;
  channel: ConsentChannel | null;
  sentTo: string | null;
  canResendAt: string | null;
}

/**
 * Where the consent request has got to.
 *
 * Polled by the waiting room, which promises to update the moment a parent
 * approves and had nothing checking — so an approved student sat there
 * permanently. Deliberately cheaper than `/api/me`, which recomputes readiness.
 */
export function fetchParentConsentState(): Promise<ParentConsentState> {
  return apiRequest<ParentConsentState>("/api/me/parent-consent", { authenticated: true });
}
